import type { ReactNode } from "react";

import { cx } from "@/lib/cx";
import { enterProps } from "@/ui/motion";
import { TONE_TEXT, type Tone } from "./tones";

export type StatusTone = "neutral" | "offline" | "error" | "success";

export type StatusPanelProps = {
  tone?: StatusTone;
  /** A compact glyph for the state. */
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Buttons; keep to one primary and at most one secondary. */
  actions?: ReactNode;
  /** Fills the available height and centres itself — for whole-screen states. */
  fill?: boolean;
  className?: string;
};

/**
 * Status semantics mapped onto the complete shared tone recipe. The glyph is
 * a colour channel of its own; a decorative tinted tile would spend a second.
 */
const TONES: Record<StatusTone, Tone> = {
  neutral: "neutral",
  offline: "streak",
  error: "danger",
  success: "accent",
};

/**
 * The one component for "there is nothing here yet", "this failed" and
 * "we cannot reach the server". Keeping them in one place is what stops an
 * outage from being mistaken for an empty list.
 *
 * Every state keeps the same compact anchor and copy hierarchy. The glyph is
 * capped at 40px and the state occupies only the space its message needs.
 */
export function StatusPanel({
  tone = "neutral",
  icon,
  title,
  description,
  actions,
  fill = false,
  className,
}: StatusPanelProps) {
  return (
    <div
      {...enterProps("content")}
      role={tone === "error" || tone === "offline" ? "alert" : undefined}
      className={cx(
        "flex flex-col items-center justify-center gap-2 p-3 text-center",
        fill && "min-h-full flex-1",
        className,
      )}
    >
      {icon ? (
        <span
          {...(tone === "neutral" || tone === "success" ? enterProps("status") : {})}
          aria-hidden
          className={cx("flex size-10 shrink-0 items-center justify-center", TONE_TEXT[TONES[tone]])}
        >
          {icon}
        </span>
      ) : null}

      <div className="flex max-w-md flex-col gap-2">
        <h2 className="text-section text-balance [overflow-wrap:anywhere] text-ink">{title}</h2>
        {description ? <p className="text-caption text-muted">{description}</p> : null}
      </div>

      {actions ? (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
